/**
 * lib/runMarker.js — RC 1.49.7 W.2 — generic in-flight run marker.
 *
 * Persists "a run is in flight" to sessionStorage, keyed by
 * (action_code, userId, jobId, sourceId). Survives tab switches and
 * same-tab page refreshes; expires after 1 hour.
 *
 * Distinct from `variantFitGuard.js`, which caches the COMPLETED
 * result of a transient cv_variant fit. They coexist and serve
 * different lifecycle phases:
 *   • runMarker: "a run is started but not yet terminal" — the
 *     mount-time recovery hook in AnalyseFitSection / future
 *     Builder polls the marker's run_id and rebinds the in-flight
 *     UI without re-submitting (and re-charging).
 *   • variantFitGuard: "a transient run completed; here is the
 *     canonical wire response" — used to rehydrate the section
 *     across cross-section re-mounts when the analyse_runs row
 *     intentionally was NOT persisted to job_scores (Stage 2).
 *
 * Hard rails:
 *   • Source-scope is enforced by the storage key alone (matches
 *     the variantFitGuard contract; same wisdom from RC 1.49.7
 *     Slice 6c hotfix). Reads do NOT validate inner billing fields.
 *   • Failures (sessionStorage quota / Safari private mode /
 *     malformed JSON) are fail-open: read returns null; write
 *     returns false. The mount-recovery branch degrades to "no
 *     in-flight run" rather than blocking the user.
 *
 * Generic across action codes: W.2 ships with `analyse_fit` only;
 * W.3 (cv_variant) and W.4 (cover_letter) reuse this module
 * unchanged by passing their own action code.
 */

const TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Build the storage key. Returns null if any required field is
 * missing — caller treats null-key as "no marker possible" and
 * skips the read/write.
 */
export function runMarkerKey(actionCode, userId, jobId, sourceId) {
  if (!actionCode || !userId || !jobId || !sourceId) return null;
  return `ndor.run.${actionCode}.${userId}.${jobId}.${sourceId}`;
}

/**
 * Write a marker. Returns true on success, false on storage error.
 *
 * Required fields:
 *   • actionCode  — e.g. 'analyse_fit'
 *   • userId, jobId, sourceId — scoping triple
 *   • runId       — the backend run row id (from the 202 response)
 *
 * Optional fields stored in the payload (passed verbatim to the
 * mount-recovery hook):
 *   • startedAt   — ms epoch (defaults to Date.now())
 *   • status      — 'queued' | 'running' | 'complete' | etc.
 *   • lastSeenAt  — ms epoch of most recent successful poll
 */
export function writeRunMarker({
  actionCode, userId, jobId, sourceId,
  runId, startedAt, status, lastSeenAt,
}) {
  const key = runMarkerKey(actionCode, userId, jobId, sourceId);
  if (!key || !runId) return false;
  const payload = {
    runId,
    startedAt: typeof startedAt === 'number' ? startedAt : Date.now(),
    status: status || 'running',
    lastSeenAt: typeof lastSeenAt === 'number' ? lastSeenAt : Date.now(),
    cachedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(key, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Read a fresh marker scoped to (action, user, job, source).
 *
 * Returns the parsed payload iff:
 *   • the entry exists,
 *   • cachedAt is within TTL_MS,
 *   • payload.runId is a non-empty string.
 *
 * Returns null otherwise. Never throws.
 */
export function readRunMarker({ actionCode, userId, jobId, sourceId }) {
  const key = runMarkerKey(actionCode, userId, jobId, sourceId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt) || 0;
    if (!cachedAt) return null;
    if (Date.now() - cachedAt >= TTL_MS) return null;
    if (typeof parsed?.runId !== 'string' || !parsed.runId) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Clear the marker for (action, user, job, source). Idempotent.
 * Called on terminal status (complete / failed / cancelled) so the
 * next mount sees a clean slate.
 */
export function clearRunMarker({ actionCode, userId, jobId, sourceId }) {
  const key = runMarkerKey(actionCode, userId, jobId, sourceId);
  if (!key) return false;
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Update the marker's status / lastSeenAt without changing scope.
 * Called from the polling loop on each successful poll so a
 * mount-time recovery hook can read the most recent observed
 * status and progress label without re-polling.
 */
export function bumpRunMarker({
  actionCode, userId, jobId, sourceId, status,
}) {
  const existing = readRunMarker({ actionCode, userId, jobId, sourceId });
  if (!existing) return false;
  return writeRunMarker({
    actionCode, userId, jobId, sourceId,
    runId: existing.runId,
    startedAt: existing.startedAt,
    status: status || existing.status,
    lastSeenAt: Date.now(),
  });
}

export const RUN_MARKER_TTL_MS = TTL_MS;
